import React, { useState, useEffect } from "react";
import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadIcon from "@mui/icons-material/Upload";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { apiList } from "../../../../helpers/apiList";
import { toast } from "react-toastify";
import CustomAlert from "../../../../helpers/CustomAlert";
import { axiosPostApi } from "../../../../helpers/interceptorServices";
import DeleteFranchiseConfirmationModal from "../../FranchiseMaster/DeleteFranchiseConfirmationModal";
import confetti from "canvas-confetti";
import { FaEye } from "react-icons/fa";
import Correct from "../../../../assets/Icons/correct.svg";

const FranchiseUploadDoc = ({
  payloadData,
  setPayloadData,
  loggedInUserName,
  handleBack,
  CloseCreate,
  fetchCustomerData,
  action,
  editData,
  documents,
  setDocuments,
  initialDocuments,
}) => {
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [isFromUrl, setIsFromUrl] = useState(false);
  const [missingDocs, setMissingDocs] = useState([]);
  const [uploadErrors, setUploadErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [hoveredDocId, setHoveredDocId] = useState(null);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const isViewMode = action === "view";
  // const isDeleteMode = action === "delete";
  // const isEditMode = action === "modify";

  useEffect(() => {
    if (action !== "add" && editData) {
      setDocuments((prevDocs) =>
        prevDocs.map((doc) => {
          const imageUrl = editData[doc.editDataKey];
          if (!imageUrl) return doc;

          // Determine file type
          let fileType = "application/pdf";
          const lowerUrl = imageUrl.toLowerCase();
          if (lowerUrl.includes(".jpg") || lowerUrl.includes(".jpeg"))
            fileType = "image/jpeg";
          else if (lowerUrl.includes(".png")) fileType = "image/png";
          else if (lowerUrl.includes(".gif")) fileType = "image/gif";

          // Extract clean file name
          const fileName =
            new URLSearchParams(imageUrl.split("?")[1]).get("fileName") ||
            imageUrl.split("/").pop() ||
            `${doc.name}.${fileType.includes("image") ? "jpg" : "pdf"}`;

          // Fetch file size directly from backend using matching key

          const fileSizeKey = doc.editDataKey.replace("FilePath", "FileSize");
          const backendFileSize = editData[fileSizeKey];

          return {
            ...doc,
            uploaded: true,
            fileBase64: imageUrl,
            fileName,
            fileType,
            fileSize: backendFileSize
              ? `${(backendFileSize / 1024).toFixed(1)} KB`
              : "N/A",
            isFromUrl: true,
          };
        })
      );
    }
  }, [action, editData]);

  const handleUpload = (event, docId) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
      ];
      const MAX_SIZE_KB = 500;

      setUploadErrors((prev) => ({ ...prev, [docId]: "" }));

      if (!validTypes.includes(file.type)) {
        setUploadErrors((prev) => ({
          ...prev,
          [docId]: "Only image and PDF files are allowed.",
        }));
        return;
      }

      const fileSizeKB = file.size / 1024;
      if (fileSizeKB > MAX_SIZE_KB) {
        setUploadErrors((prev) => ({
          ...prev,
          [docId]: "File size should not exceed 500KB.",
        }));
        return;
      }

      const docMeta = initialDocuments.find((doc) => doc.id === docId);
      const key = docMeta?.editDataKey?.replace("FilePath", "");

      if (!key) return;

      setPayloadData((prev) => ({
        ...prev,
        [key]: file,
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === docId
              ? {
                  ...doc,
                  uploaded: true,
                  fileSize: fileSizeKB.toFixed(2) + " KB",
                  fileName: file.name,
                  fileType: file.type,
                  fileBase64: base64String,
                  isFromUrl: false,
                  status: "Approved",
                }
              : doc
          )
        );

        setMissingDocs((prev) => prev.filter((id) => id !== docId));
      };

      reader.readAsDataURL(file);
    }
  };

  const handleDelete = (docId) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === docId
          ? {
              ...doc,
              uploaded: false,
              fileBase64: null,
              fileSize: null,
              fileName: null,
              fileType: null,
              isFromUrl: false,
              status: null,
            }
          : doc
      )
    );

    setPayloadData((prev) => ({
      ...prev,
      documents: (prev.documents || []).filter((doc) => doc.id !== docId),
    }));

    setUploadErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[docId];
      return newErrors;
    });
  };

  const handleClosePreview = () => {
    setPreviewDoc(null);
    setPreviewName("");
    setPreviewType("");
    setIsFromUrl(false);
  };

  const validateAndProceed = (callback) => {
    const missing = documents
      .filter((doc) => !doc.uploaded)
      .map((doc) => doc.id);
    if (missing.length > 0) {
      setMissingDocs(missing);
    } else {
      setMissingDocs([]);
      callback();
    }
  };

  const renderPreviewContent = () => {
    if (previewType.includes("pdf")) {
      const isPdfUrl = isFromUrl && !previewDoc.startsWith("data:");
      return (
        <div>
          {isPdfUrl ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div
                style={{
                  marginBottom: "20px",
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                PDF preview may not be available due to server restrictions.
              </div>
              <div style={{ marginTop: "20px", border: "1px solid #ddd" }}>
                <iframe
                  src={previewDoc}
                  width="100%"
                  height="400px"
                  title="PDF Preview"
                  style={{ border: "none" }}
                  onError={() => console.log("PDF iframe failed to load")}
                />
              </div>
            </div>
          ) : (
            <div>
              <iframe
                src={previewDoc}
                width="100%"
                height="500px"
                title="PDF Preview"
                style={{ border: "none" }}
              />
            </div>
          )}
        </div>
      );
    } else if (previewType.includes("image")) {
      return (
        <div>
          <img
            src={previewDoc}
            alt="Document Preview"
            style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }}
          />
        </div>
      );
    } else {
      return (
        <div>
          <p>Unsupported file format for preview.</p>
        </div>
      );
    }
  };

  const handleAddCustomer = () => {
    setLoading(true);
    const formData = new FormData();
    const {
      gstCertificate,
      incorporationCertificate,
      aadharFront,
      aadharBack,
      pan,
      authPersonPan,
      boardResolution,
      ...restFields
    } = payloadData;
    formData.append("request", JSON.stringify(restFields));

    if (gstCertificate) formData.append("gstCertificate", gstCertificate);
    if (incorporationCertificate)
      formData.append("incorporationCertificate", incorporationCertificate);
    if (aadharFront) formData.append("aadharFront", aadharFront);
    if (aadharBack) formData.append("aadharBack", aadharBack);
    if (pan) formData.append("panFile", pan);
    if (authPersonPan) formData.append("authPersonPanFile", authPersonPan);
    if (boardResolution) formData.append("boardResolution", boardResolution);

    let apiUrl = "";
    let successMessage = "";

    if (action === "add") {
      apiUrl = apiList.CREATE_FRANCHISE_MASTER;
      successMessage = "Franchise added successfully";
    } else if (action === "modify") {
      apiUrl = apiList.MODIFY_FRANCHISE_MASTER;
      successMessage = "Franchise details modified successfully";
    }

    axiosPostApi(apiUrl, formData)
      .then((response) => {
        setLoading(false);
        if (response.status === 200 && response.data.status === true) {
          CustomAlert({
            title: "Success",
            text: response?.data?.message || successMessage,
            icon: "success",
            confirmButtonText: "Ok",
          });

          const canvas = document.createElement("canvas");
          canvas.style.position = "fixed";
          canvas.style.top = "0";
          canvas.style.left = "0";
          canvas.style.width = "100%";
          canvas.style.height = "100%";
          canvas.style.zIndex = "3000";
          document.body.appendChild(canvas);

          const confettiInstance = confetti.create(canvas, {
            resize: true,
            useWorker: true,
          });

          confettiInstance({
            particleCount: 250,
            spread: 100,
            origin: { y: 0.6 },
          });

          setTimeout(() => {
            document.body.removeChild(canvas);
          }, 3000);

          CloseCreate();
          fetchCustomerData(1);
        } else {
          CustomAlert({
            title: "Failed",
            text:
              response?.data?.message ||
              "There was an issue processing the request.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error:", error);
        CustomAlert({
          title: "Error",
          text: "An error occurred while processing.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      });
  };

  const handleAuthorize = () => {
    setIsAuthModalOpen(true);
  };

  const handleAuthSubmit = async (action, remarks) => {
    let status;
    let actionText;

    switch (action) {
      case "approve":
        status = "1";
        actionText = "authorized";
        break;
      case "block":
        status = "3";
        actionText = "blocked";
        break;
      case "reject":
        status = "5";
        actionText = "rejected";
        break;
      default:
        status = "5";
        actionText = "rejected";
    }

    const requestBody = {
      username: loggedInUserName,
      franchiseCode: payloadData?.franchiseCode || "",
      status: status,
      adminRemark: remarks || "",
    };

    try {
      const response = await axiosPostApi(
        apiList.AUTH_FRANCHISE_DATA,
        requestBody
      );
      if (response?.status === 200 && response?.data?.respCode === "00") {
        CustomAlert({
          title: "Success",
          text: `Franchise ${actionText} successfully!`,
          icon: "success",
          confirmButtonText: "Ok",
        });

        if (action === "approve") {
          const canvas = document.createElement("canvas");
          canvas.style.position = "fixed";
          canvas.style.top = "0";
          canvas.style.left = "0";
          canvas.style.width = "100%";
          canvas.style.height = "100%";
          canvas.style.zIndex = "3000";
          document.body.appendChild(canvas);

          const confettiInstance = confetti.create(canvas, {
            resize: true,
            useWorker: true,
          });

          confettiInstance({
            particleCount: 250,
            spread: 100,
            origin: { y: 0.6 },
          });

          setTimeout(() => {
            document.body.removeChild(canvas);
          }, 3000);
        }

        fetchCustomerData(1);
        CloseCreate();
      } else {
        CustomAlert({
          title: `Failed to ${actionText} Franchise`,
          text:
            response.data?.message || "Invalid credentials. Please try again!",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      console.error(`Error ${actionText} Franchise`, error);
      toast.error(`Failed to ${actionText} Franchise.`);
    }

    setIsAuthModalOpen(false);
  };

  return (
    <>
      <form>
        <Box
          sx={{
            p: 4,
            fontFamily: "'Roboto', sans-serif",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            paddingTop: 0,
          }}
        >
          {documents.map((doc) => (
            <Card
              key={doc.id}
              sx={{
                width: "100%",
                // minHeight: 200,
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                position: "relative",
              }}
              onMouseEnter={() => isViewMode && setHoveredDocId(doc.id)}
              onMouseLeave={() => isViewMode && setHoveredDocId(null)}
            >
              <CardContent sx={{ pb: "16px !important" }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#FF702B", mb: 1, fontSize: "16px" }}
                >
                  {doc.name}
                </Typography>
                {action === "view" ||
                action === "delete" ||
                action === "auth" ||
                doc.uploaded ? (
                  <div class="flex gap-1 items-center text-green-600">
                    <img alt="Success" src={Correct} />
                    <span>Uploaded</span>
                  </div>
                ) : (
                  <Typography variant="body2" sx={{ fontSize: "14px" }}>
                    • Maximum allowable file size 500KB
                  </Typography>
                )}
                {action === "view" ||
                action === "delete" ||
                action === "auth" ||
                action === "modify" ? (
                  doc.uploaded ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      {/* ✅ File Size & Actions */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        {doc.fileSize && (
                          <span className="text-[#6a7282] text-sm">
                            {doc.fileSize}
                          </span>
                        )}
                        {/* 👁 View */}
                        <FaEye
                          className="text-gray-600 cursor-pointer hover:text-orange-500"
                          onClick={() => {
                            if (!doc) return;

                            // Case 1: File object (uploaded)
                            if (doc instanceof File) {
                              const url = URL.createObjectURL(doc);
                              window.open(url, "_blank");
                              return;
                            }

                            // Case 2: URL from backend
                            if (doc.fileBase64?.startsWith("http")) {
                              window.open(doc.fileBase64, "_blank");
                              return;
                            }

                            // Case 3: Base64 string
                            if (doc.fileBase64) {
                              let base64Data = doc.fileBase64;
                              if (!base64Data.startsWith("data:")) {
                                base64Data = `data:${
                                  doc.fileType || "application/pdf"
                                };base64,${base64Data}`;
                              }
                              const newTab = window.open();
                              newTab.document.write(
                                `<iframe src="${base64Data}" frameborder="0" style="width:100%;height:100vh;"></iframe>`
                              );
                            }
                          }}
                        />
                        {/* 📄 File Name */}
                        <Typography sx={{ fontSize: "14px", color: "#6a7282" }}>
                          📄{" "}
                          {doc.fileName?.split("fileName=")[1] || doc.fileName}
                        </Typography>
                      </Box>
                      {/* 🟠 Allow replace if modify mode */}
                      {action === "modify" && (
                        <>
                          <input
                            accept="image/*,.pdf"
                            type="file"
                            id={`replace-${doc.id}`}
                            style={{ display: "none" }}
                            onChange={(e) => handleUpload(e, doc.id)} // same upload handler
                          />
                          <label htmlFor={`replace-${doc.id}`}>
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={<UploadIcon />}
                              sx={{
                                borderColor: "#FF702B",
                                color: "#FFFFFF",
                                backgroundColor: "#FF702B",
                                height: "35px",
                              }}
                            >
                              Upload
                            </Button>
                          </label>
                        </>
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ color: "#999", fontStyle: "italic" }}>
                      No document uploaded
                    </Typography>
                  )
                ) : doc.uploaded ? (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <span className="text-[#6a7282] text-sm">
                          {doc?.fileSize}
                        </span>

                        <FaEye
                          className="text-gray-600 cursor-pointer hover:text-orange-500"
                          onClick={() => {
                            if (!doc) return;

                            // Case 1: if it's a File object (from input)
                            if (doc instanceof File) {
                              const url = URL.createObjectURL(doc);
                              window.open(url, "_blank");
                              return;
                            }

                            // Case 2: if it has base64 (from API or saved data)
                            if (doc.fileBase64) {
                              let base64Data = doc.fileBase64;

                              // Add proper prefix if missing
                              if (!base64Data.startsWith("data:")) {
                                base64Data = `data:${
                                  doc.fileType || "application/pdf"
                                };base64,${base64Data}`;
                              }

                              // Open new tab with an iframe (works for images & pdf)
                              const newTab = window.open();
                              newTab.document.write(
                                `<iframe src="${base64Data}" frameborder="0" style="width:100%;height:100vh;"></iframe>`
                              );
                              return;
                            }

                            console.error("Invalid document format", doc);
                          }}
                        />

                        <IconButton
                          sx={{ color: "#DC3545", ml: 1 }}
                          onClick={() => handleDelete(doc.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Box>
                        <span className="text-gray-500 text-sm">
                          📄 {doc.fileName}
                        </span>
                      </Box>
                    </Box>
                    {doc.status === "Rejected" && (
                      <>
                        <Typography sx={{ color: "#DC3545", mb: 1 }}>
                          Remarks: Upload proper image
                        </Typography>
                        <input
                          accept="image/*,.pdf"
                          type="file"
                          id={`upload-${doc.id}`}
                          style={{ display: "none" }}
                          onChange={(e) => handleUpload(e, doc.id)}
                        />
                        <label htmlFor={`upload-${doc.id}`}>
                          <Button
                            variant="outlined"
                            component="span"
                            startIcon={<UploadIcon />}
                            sx={{
                              borderColor: "#FF702B",
                              color: "#FF702B",
                              backgroundColor: "white",
                              "&:hover": {
                                borderColor: "#009CAD",
                                color: "#009CAD",
                              },
                            }}
                          >
                            Reupload Document
                          </Button>
                        </label>
                      </>
                    )}
                  </Box>
                ) : (
                  <>
                    <input
                      accept="image/*,.pdf"
                      type="file"
                      id={`upload-${doc.id}`}
                      style={{ display: "none" }}
                      onChange={(e) => handleUpload(e, doc.id)}
                    />
                    <label htmlFor={`upload-${doc.id}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        sx={{
                          borderColor: "#FF702B",
                          color: "#FFFFFF",
                          backgroundColor: "#FF702B",
                          height: "35px",
                        }}
                      >
                        Upload
                      </Button>
                    </label>
                    {(uploadErrors[doc.id] || missingDocs.includes(doc.id)) && (
                      <Typography
                        sx={{ color: "red", fontSize: "0.85rem", mt: 1 }}
                      >
                        {uploadErrors[doc.id]
                          ? uploadErrors[doc.id]
                          : `${doc.name} is required.`}
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>

        <Dialog
          open={Boolean(previewDoc)}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography>{previewName}</Typography>
              <IconButton onClick={handleClosePreview}>
                <HighlightOffIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {loading ? (
              <Box sx={{ textAlign: "center", p: 2 }}>
                <CircularProgress />
                <Typography>Loading document preview...</Typography>
              </Box>
            ) : (
              renderPreviewContent()
            )}
          </DialogContent>
        </Dialog>

        {/* <Box>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              fontSize: "14px",
              color: "#1A202C",
              cursor: "pointer",
              paddingLeft:"32px",
               fontWeight: 500,
            }}
          >
           <span>
             By submitting this form, you consent to receive marketing communications
             from us via SMS/RCS/Email/Calls.
           </span>
          </label>
        </Box> */}

        <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
          <button
            type="button"
            onClick={handleBack}
            className="btn btn-submit w-[133px] !rounded-lg"
          >
            Back
          </button>
          {action === "view" ? (
            <button
              type="button"
              onClick={CloseCreate}
              className="btn btn-submit w-[133px] !rounded-lg"
            >
              Close
            </button>
          ) : action === "delete" ? (
            <button
              type="button"
              onClick={openModal}
              className="btn btn-submit w-[133px] !rounded-lg"
            >
              Delete
            </button>
          ) : action === "auth" ? (
            <button
              type="button"
              onClick={() => handleAuthorize()}
              className="btn btn-submit w-[133px] !rounded-lg"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={() => validateAndProceed(handleAddCustomer)}
              className="btn btn-submit w-[133px] !rounded-lg"
            >
              {loading ? (
                <CircularProgress
                  sx={{ width: "20px !important", height: "20px !important" }}
                />
              ) : action === "modify" ? (
                "Update"
              ) : (
                "Submit"
              )}
            </button>
          )}
        </Box>
      </form>
      <DeleteFranchiseConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        editData={editData}
        fetchCustomerData={fetchCustomerData}
        CloseCreate={CloseCreate}
      />
      {isAuthModalOpen && (
        <AuthorizationConfirmationModal
          show={isAuthModalOpen}
          handleClose={() => setIsAuthModalOpen(false)}
          handleSubmit={handleAuthSubmit}
        />
      )}
    </>
  );
};

const AuthorizationConfirmationModal = ({
  show,
  handleClose,
  handleSubmit,
}) => {
  const [action, setAction] = useState("approve");
  const [adminRemark, setRemarks] = useState("");
  const [error, setError] = useState("");

  if (!show) return null;

  const onSubmit = () => {
    if (!adminRemark.trim()) {
      setError("Remark is required");
      return;
    }
    setError("");
    handleSubmit(action, adminRemark);
  };

  const handleRemarkChange = (e) => {
    setRemarks(e.target.value);
    if (error && e.target.value.trim()) {
      setError("");
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
      }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          p: 5,
          borderRadius: "40px",
          width: "90%",
          maxWidth: "600px",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{ fontWeight: "bold", fontSize: "24px", color: "#1A202C" }}
        >
          Are you sure?
        </Typography>
        <Typography
          sx={{
            color: "#1A202C",
            fontSize: "18px",
            fontWeight: "normal",
            mt: 1,
          }}
        >
          Do you want to authorize the Franchise?
        </Typography>

        <Box
          sx={{
            my: 3,
            display: "flex",
            justifyContent: "center",
            gap: 4,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "16px",
              color: "#1A202C",
            }}
          >
            <input
              type="radio"
              value="approve"
              checked={action === "approve"}
              onChange={() => setAction("approve")}
              style={{
                width: "20px",
                height: "20px",
                accentColor: "#F97316",
              }}
            />
            Approve
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "16px",
              color: "#1A202C",
            }}
          >
            <input
              type="radio"
              value="reject"
              checked={action === "reject"}
              onChange={() => setAction("reject")}
              style={{
                width: "20px",
                height: "20px",
                accentColor: action === "reject" ? "red" : "#00C4B4",
              }}
            />
            Reject
          </label>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              display: "block",
              textAlign: "left",
              fontSize: "16px",
              color: "#1A202C",
            }}
          >
            Remark <span style={{ color: "red" }}>*</span>
          </Typography>
          <textarea
            placeholder="Enter your remarks here..."
            value={adminRemark}
            onChange={handleRemarkChange}
            style={{
              width: "100%",
              height: "100px",
              border: "1px solid #F97316",
              borderRadius: "10px",
              padding: "5px",
              boxSizing: "border-box",
              fontSize: "14px",
            }}
          />
          {error && (
            <Typography
              sx={{
                color: "red",
                fontSize: "14px",
                textAlign: "left",
                mt: 1,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            onClick={handleClose}
            sx={{
              padding: "10px",
              width: "120px",
              border: "1px solid #F97316",
              borderRadius: "20px",
              backgroundColor: "white",
              color: "#F97316",
              "&:hover": {
                backgroundColor: "#f0f0f0",
                borderColor: "#F97316",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            sx={{
              padding: "10px",
              width: "120px",
              border: "none",
              borderRadius: "20px",
              backgroundColor: "#F97316",
              color: "white",
              "&:hover": {
                backgroundColor: "white",
                color: "#F97316",
                border: "1px solid #F97316",
              },
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FranchiseUploadDoc;
